import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Typography } from '@mui/material';
import axiosInstance from '@/setup/axios/axiosInstance';

import { AppPagination, EmployeeDisplay } from '@/__core/components';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

export type AllocationListHandle = {
  requestRefresh: () => void;
};

interface AllocationListProps {
  canUpdate?: boolean;
  companyId?: string;
  searchQuery?: string;
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onEditOrder?: (orderId: string, orderNo: string, customerName: string, items: AllocationApiItem[]) => void;
}

interface AllocationApiEmployee {
  id?: string;
  name?: string | null;
  nickname?: string | null;
  image1?: string | null;
  user?: {
    id?: string;
    avatar?: string | null;
  } | null;
}

interface AllocationApiItem {
  allocation_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  allocated_qty: string;
  uom_id?: string;
  uom_name: string;
  store_name: string;
  created_by?: AllocationApiEmployee | null;
  created_by_name?: string | null;
  created_at?: string | null;
  updated_by?: AllocationApiEmployee | null;
  updated_by_name?: string | null;
  updated_at?: string | null;
}

interface AllocationApiResponse {
  order_id: string;
  order_no: string;
  customer_name?: string;
  company_id: string;
  company_name: string;
  items?: AllocationApiItem[];
}

interface AllocationDisplayRow {
  id: string;
  rowType: 'group' | 'item';
  orderId: string;
  orderNo: string;
  customerName: string;
  itemCode: string;
  itemName: string;
  uomName: string;
  allocatedQty: number;
  createdBy?: AllocationApiEmployee | null;
  createdByName: string;
  createdAt?: string | null;
  updatedBy?: AllocationApiEmployee | null;
  updatedByName: string;
  updatedAt?: string | null;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
};

const AllocationList = forwardRef<AllocationListHandle, AllocationListProps>(function AllocationList(
  { canUpdate = true, companyId, searchQuery, pagination, onPageChange, onEditOrder },
  ref
) {
  const [reloadTick, setReloadTick] = useState(0);
  const [apiOrders, setApiOrders] = useState<AllocationApiResponse[]>([]);

  useEffect(() => {
    let active = true;

    const fetchAllocations = async () => {
      try {
        const params: Record<string, string> = {};
        if (companyId && companyId !== 'global' && companyId !== 'all') {
          params.company = companyId;
        }
        if (searchQuery) params.search = searchQuery;

        const response = await axiosInstance.get('/inventory/allocations/', { params });
        if (!active) return;
        const data = response?.data?.results || response?.data || [];
        setApiOrders(Array.isArray(data) ? data : []);
      } catch {
        if (!active) return;
        setApiOrders([]);
      }
    };

    fetchAllocations();
    return () => { active = false; };
  }, [companyId, searchQuery, reloadTick]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => setReloadTick((current) => current + 1),
    }),
    []
  );

  const displayRows = useMemo<AllocationDisplayRow[]>(() => {
    const rows: AllocationDisplayRow[] = [];

    apiOrders.forEach((order) => {
      const items = order.items || [];
      rows.push({
        id: `group-${order.order_id}`,
        rowType: 'group',
        orderId: order.order_id,
        orderNo: order.order_no || '-',
        customerName: order.customer_name || '',
        itemCode: '',
        itemName: '',
        uomName: '',
        allocatedQty: 0,
        createdBy: null,
        createdByName: '',
        createdAt: null,
        updatedBy: null,
        updatedByName: '',
        updatedAt: null,
      });

      items.forEach((item) => {
        rows.push({
          id: `item-${item.allocation_id}`,
          rowType: 'item',
          orderId: order.order_id,
          orderNo: order.order_no,
          customerName: order.customer_name || '',
          itemCode: item.item_code || '-',
          itemName: item.item_name || '-',
          uomName: item.uom_name || '-',
          allocatedQty: toNumber(item.allocated_qty),
          createdBy: item.created_by,
          createdByName: item.created_by_name || item.created_by?.name || '',
          createdAt: item.created_at,
          updatedBy: item.updated_by,
          updatedByName: item.updated_by_name || item.updated_by?.name || '',
          updatedAt: item.updated_at,
        });
      });
    });

    return rows;
  }, [apiOrders]);

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: AllocationDisplayRow) => React.ReactNode;
  }> = [
    {
      id: 'orderNo',
      label: 'Đơn hàng',
      width: '220px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? (
          <Typography sx={{ fontWeight: 700, color: '#3730a3' }}>
            {row.orderNo}{row.customerName ? ` - ${row.customerName}` : ''}
          </Typography>
        )
        : null,
    },
    {
      id: 'itemCode',
      label: 'Mã VT',
      width: '160px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group' ? null : <Typography sx={{ fontWeight: 600 }}>{row.itemCode}</Typography>,
    },
    {
      id: 'itemName',
      label: 'Tên vật tư',
      width: '280px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group' ? null : row.itemName,
    },
    {
      id: 'uomName',
      label: 'ĐVT',
      width: '100px',
      align: 'center',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group' ? null : row.uomName,
    },
    {
      id: 'allocatedQty',
      label: 'Số lượng đã gán',
      width: '150px',
      align: 'right',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? null
        : row.allocatedQty.toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 3 }),
    },
    {
      id: 'createdByName',
      label: 'Người tạo',
      width: '190px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? null
        : (
          <EmployeeDisplay
            name={row.createdByName || undefined}
            nickname={row.createdBy?.nickname}
            image1={row.createdBy?.image1}
            avatar={row.createdBy?.user?.avatar}
            employeeId={row.createdBy?.id}
            userId={row.createdBy?.user?.id}
            fallbackText='-'
          />
        ),
    },
    {
      id: 'createdAt',
      label: 'Ngày tạo',
      width: '165px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? null
        : formatDateTime(row.createdAt),
    },
    {
      id: 'updatedByName',
      label: 'Người cập nhật',
      width: '190px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? null
        : (
          <EmployeeDisplay
            name={row.updatedByName || undefined}
            nickname={row.updatedBy?.nickname}
            image1={row.updatedBy?.image1}
            avatar={row.updatedBy?.user?.avatar}
            employeeId={row.updatedBy?.id}
            userId={row.updatedBy?.user?.id}
            fallbackText='-'
          />
        ),
    },
    {
      id: 'updatedAt',
      label: 'Ngày cập nhật',
      width: '165px',
      render: (_: unknown, row: AllocationDisplayRow) => row.rowType === 'group'
        ? null
        : formatDateTime(row.updatedAt),
    },
  ];

  const totalCount = pagination?.count || displayRows.length;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || DEFAULT_PAGE_SIZE;
  const totalPages = pagination?.totalPages || Math.max(Math.ceil(totalCount / pageSize), 1);

  return (
    <Box sx={sharedListStyles.container}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataTableModule
          data={displayRows}
          columns={columns}
          leftFilterWidth={0}
          onRowSelect={() => undefined}
          onSelectAll={() => undefined}
          onRowClick={(row: unknown) => {
            const typedRow = row as AllocationDisplayRow;
            if (typedRow.rowType === 'group' && onEditOrder) {
              const order = apiOrders.find((o) => o.order_id === typedRow.orderId);
              onEditOrder(
                typedRow.orderId,
                typedRow.orderNo,
                typedRow.customerName,
                order?.items || [],
              );
            }
          }}
          getRowId={(row) => (row as AllocationDisplayRow).id}
          isRowSelected={() => false}
          areAllRowsSelected={false}
          areSomeRowsSelected={false}
          showCheckbox={false}
          getRowClassName={(row) => {
            const typedRow = row as AllocationDisplayRow;
            return typedRow.rowType === 'group' ? 'group-row' : 'item-row-indented';
          }}
        />
        <AppPagination
          count={totalCount}
          page={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          itemLabel='phân bổ'
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  );
});

export default AllocationList;
